import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Heart, MessageSquare, Send, Trash2, X } from "lucide-react";
import { Header } from "@/components/Header";
import { JobBoard } from "@/components/JobBoard";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const ago = (iso) => {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  return new Date(iso).toLocaleDateString();
};

const Avatar = ({ person, size = "h-9 w-9" }) =>
  person?.picture ? (
    <img src={person.picture} alt={person.name} className={`${size} shrink-0 rounded-full object-cover`} />
  ) : (
    <span className={`${size} grid shrink-0 place-items-center rounded-full bg-neutral-900 text-xs font-bold text-white`}>
      {(person?.name || "?")[0].toUpperCase()}
    </span>
  );

export default function Forum() {
  const { user } = useAuth();
  const [channels, setChannels] = useState([]);
  const [active, setActive] = useState("all");
  const [posts, setPosts] = useState([]);
  const [open, setOpen] = useState(null);
  const [reply, setReply] = useState("");
  const [draft, setDraft] = useState({ title: "", body: "" });
  const [composing, setComposing] = useState(false);

  const loadChannels = useCallback(() => {
    apiFetch("/forum/channels").then((r) => setChannels(r.channels)).catch(() => {});
  }, []);

  const loadPosts = useCallback(async () => {
    const res = await apiFetch(`/forum/posts?channel=${active}`).catch(() => ({ posts: [] }));
    setPosts(res.posts);
  }, [active]);

  useEffect(() => {
    loadChannels();
  }, [loadChannels]);
  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  const post = async (e) => {
    e.preventDefault();
    try {
      await apiFetch("/forum/posts", {
        method: "POST",
        body: JSON.stringify({ channel: active === "all" ? "general" : active, ...draft }),
      });
      toast.success("Posted");
      setDraft({ title: "", body: "" });
      setComposing(false);
      loadPosts();
      loadChannels();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const like = async (p) => {
    const res = await apiFetch(`/forum/posts/${p.post_id}/like`, { method: "POST" });
    setPosts((prev) =>
      prev.map((x) => (x.post_id === p.post_id ? { ...x, liked: res.liked, like_count: res.like_count } : x))
    );
    setOpen((o) => (o?.post_id === p.post_id ? { ...o, liked: res.liked, like_count: res.like_count } : o));
  };

  const openPost = async (p) => setOpen(await apiFetch(`/forum/posts/${p.post_id}`));

  const sendReply = async (e) => {
    e.preventDefault();
    try {
      await apiFetch(`/forum/posts/${open.post_id}/replies`, { method: "POST", body: JSON.stringify({ body: reply }) });
      setReply("");
      setOpen(await apiFetch(`/forum/posts/${open.post_id}`));
      loadPosts();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const removePost = async (p) => {
    try {
      await apiFetch(`/forum/posts/${p.post_id}`, { method: "DELETE" });
      toast.success("Post removed");
      setOpen(null);
      loadPosts();
      loadChannels();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-extrabold tracking-tight text-neutral-900 sm:text-4xl">Member forum</h1>
        <p className="mt-2 text-sm text-neutral-500 md:text-lg">
          Talk with other Portland tech members. Be useful, be kind.
        </p>

        <div className="mt-8 grid gap-8 lg:grid-cols-[240px_1fr]">
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="flex flex-wrap gap-2 lg:flex-col" data-testid="channel-list">
              {[{ slug: "all", name: "All channels", post_count: posts.length }, ...channels].map((c) => (
                <button
                  key={c.slug}
                  data-testid={`channel-${c.slug}`}
                  onClick={() => setActive(c.slug)}
                  className={`rounded-xl px-4 py-2.5 text-left text-sm font-semibold transition-colors duration-200 ${
                    active === c.slug ? "bg-neutral-900 text-white" : "text-neutral-700 hover:bg-neutral-100"
                  }`}
                >
                  {c.name}
                  {c.post_count > 0 && (
                    <span className={`ml-2 text-xs ${active === c.slug ? "text-white/70" : "text-neutral-400"}`}>
                      {c.post_count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </aside>

          <div>
            {active === "jobs" ? (
              <JobBoard />
            ) : (
              <>
            {composing ? (
              <form onSubmit={post} className="rounded-2xl border border-neutral-200 p-5" data-testid="compose-form">
                <input
                  required
                  data-testid="post-title"
                  value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  placeholder="What's the headline?"
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm outline-none transition-colors duration-200 focus:border-neutral-900"
                />
                <textarea
                  required
                  rows={5}
                  data-testid="post-body"
                  value={draft.body}
                  onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                  placeholder="Share the details…"
                  className="mt-3 w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm outline-none transition-colors duration-200 focus:border-neutral-900"
                />
                <div className="mt-3 flex gap-3">
                  <button
                    data-testid="post-submit"
                    className="rounded-full bg-[#B8FF2E] px-5 py-2.5 text-sm font-bold text-[#14210A] transition-colors duration-200 hover:bg-[#A2E80B]"
                  >
                    Post to {active === "all" ? "General" : channels.find((c) => c.slug === active)?.name}
                  </button>
                  <button
                    type="button"
                    onClick={() => setComposing(false)}
                    data-testid="post-cancel"
                    className="rounded-full border border-neutral-300 px-4 py-2.5 text-sm font-semibold text-neutral-700 transition-colors duration-200 hover:bg-neutral-50"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <button
                onClick={() => setComposing(true)}
                data-testid="new-post-btn"
                className="w-full rounded-2xl border border-dashed border-neutral-300 px-5 py-4 text-left text-sm font-semibold text-neutral-500 transition-colors duration-200 hover:border-neutral-500 hover:text-neutral-800"
              >
                Start a discussion…
              </button>
            )}

            <ul className="mt-6 space-y-3" data-testid="post-list">
              {posts.length === 0 && (
                <li className="rounded-2xl border border-dashed border-neutral-300 p-12 text-center text-sm text-neutral-500">
                  No posts here yet — be the first.
                </li>
              )}
              {posts.map((p) => (
                <li
                  key={p.post_id}
                  data-testid={`post-${p.post_id}`}
                  className="rounded-2xl border border-neutral-200 p-5 transition-shadow duration-200 hover:shadow-[0_4px_16px_rgba(0,0,0,0.07)]"
                >
                  <div className="flex items-start gap-3">
                    <Avatar person={p.author} />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-neutral-500">
                        <Link
                          to={`/members/${p.author.user_id}`}
                          data-testid={`author-link-${p.post_id}`}
                          className="font-semibold text-neutral-800 hover:underline"
                        >
                          {p.author.name}
                        </Link>
                        {p.author.role !== "member" && (
                          <span className="ml-1.5 rounded-full bg-neutral-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-neutral-600">
                            {p.author.role}
                          </span>
                        )}{" "}
                        · {ago(p.created_at)} · {channels.find((c) => c.slug === p.channel)?.name || p.channel}
                      </p>
                      <button onClick={() => openPost(p)} className="mt-1 block w-full text-left">
                        <h3 className="text-base font-bold text-neutral-900">{p.title}</h3>
                        <p className="mt-1 line-clamp-2 text-sm text-neutral-600">{p.body}</p>
                      </button>
                      <div className="mt-3 flex items-center gap-4">
                        <button
                          onClick={() => like(p)}
                          data-testid={`like-${p.post_id}`}
                          className="flex items-center gap-1.5 text-sm font-semibold text-neutral-500 transition-colors duration-200 hover:text-[#4D7C0F]"
                        >
                          <Heart className={`h-4 w-4 ${p.liked ? "fill-[#84CC16] text-[#4D7C0F]" : ""}`} />
                          {p.like_count}
                        </button>
                        <button
                          onClick={() => openPost(p)}
                          data-testid={`replies-${p.post_id}`}
                          className="flex items-center gap-1.5 text-sm font-semibold text-neutral-500 transition-colors duration-200 hover:text-neutral-900"
                        >
                          <MessageSquare className="h-4 w-4" /> {p.reply_count}
                        </button>
                        {(p.author.user_id === user?.user_id || user?.role === "admin") && (
                          <button
                            onClick={() => removePost(p)}
                            data-testid={`delete-post-${p.post_id}`}
                            className="ml-auto text-neutral-400 transition-colors duration-200 hover:text-[#4D7C0F]"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
              </>
            )}
          </div>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-6">
          <div
            className="max-h-[88vh] w-full max-w-2xl overflow-auto rounded-t-2xl bg-white p-6 sm:rounded-2xl"
            data-testid="post-detail"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs text-neutral-500">
                  {open.author.name} · {ago(open.created_at)}
                </p>
                <h2 className="mt-1 text-2xl font-bold tracking-tight text-neutral-900">{open.title}</h2>
              </div>
              <button onClick={() => setOpen(null)} data-testid="close-post" className="rounded-full p-2 hover:bg-neutral-100">
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-4 whitespace-pre-line leading-relaxed text-neutral-700">{open.body}</p>

            <div className="mt-5 flex items-center gap-4 border-y border-neutral-200 py-3">
              <button
                onClick={() => like(open)}
                data-testid="detail-like"
                className="flex items-center gap-1.5 text-sm font-semibold text-neutral-600 transition-colors duration-200 hover:text-[#4D7C0F]"
              >
                <Heart className={`h-4 w-4 ${open.liked ? "fill-[#84CC16] text-[#4D7C0F]" : ""}`} /> {open.like_count}
              </button>
              <span className="text-sm text-neutral-500">{open.replies.length} replies</span>
            </div>

            <ul className="mt-4 space-y-4" data-testid="reply-list">
              {open.replies.map((r) => (
                <li key={r.reply_id} className="flex gap-3">
                  <Avatar person={r.author} size="h-8 w-8" />
                  <div>
                    <p className="text-xs text-neutral-500">
                      <span className="font-semibold text-neutral-800">{r.author.name}</span> · {ago(r.created_at)}
                    </p>
                    <p className="mt-0.5 whitespace-pre-line text-sm text-neutral-700">{r.body}</p>
                  </div>
                </li>
              ))}
            </ul>

            <form onSubmit={sendReply} className="mt-5 flex gap-2">
              <input
                required
                data-testid="reply-input"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Write a reply…"
                className="flex-1 rounded-full border border-neutral-300 px-4 py-2.5 text-sm outline-none transition-colors duration-200 focus:border-neutral-900"
              />
              <button
                data-testid="reply-submit"
                className="grid h-11 w-11 place-items-center rounded-full bg-[#B8FF2E] text-[#14210A] transition-colors duration-200 hover:bg-[#A2E80B]"
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
